import styled from 'styled-components';

import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { GraphQLEditor } from '@components/graphql-editor';
import { IconButton } from '@components/icon-button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCopyContent } from '@components/icons/copy-content';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useClipboard } from '@hooks/use-clipboard';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type RequestDetailsData = {
  endpointRequestType?: string | null;
  requestHeaders?: Record<string, unknown> | null;
  requestData?: unknown;
  graphqlQuery?: string | null;
  responseData?: unknown;
  responseHeaders?: Record<string, unknown> | null;
  requestFiles?: string[];
};

export const RequestDetailsUrlRow = ({ url }: { url?: string | null }) => {
  const { t } = useScopedTranslate(locale);
  const { copied, onCopy } = useClipboard();
  if (!url) {
    return null;
  }
  return (
    <Stack space={2}>
      <Text font="bold14" color="textSecondary" text={t('url')} />
      <Card variant="gray" spaceX={3} spaceY={2}>
        <Stack orientation="horizontal" align="center" justify="space-between" space={2}>
          <Text font="regular14" text={url} ellipsisWithTooltip />
          <IconButton
            variant="ghost-purple"
            icon={copied ? <IconCheckmark /> : <IconCopyContent />}
            onClick={() => onCopy(url)}
          />
        </Stack>
      </Card>
    </Stack>
  );
};

export const RequestDetailsSections = ({ data }: { data: RequestDetailsData }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <>
      <Stack space={3}>
        <Text font="heading2" text={t('request')} />
        <RequestDetailsRequestBody data={data} />
      </Stack>
      <ResponseSection data={data} />
    </>
  );
};

export const RequestDetailsRequestBody = ({
  data,
  bodyLabel,
  headersLabel,
}: {
  data: RequestDetailsData;
  bodyLabel?: string;
  headersLabel?: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const isGraphql = (data.endpointRequestType ?? '').toLowerCase() === 'graphql';
  const query = data.graphqlQuery ?? '';
  const showQuery = isGraphql && query.trim() !== '';

  return (
    <>
      {showQuery && <QueryBlock header={t('query')} query={query} />}
      <BodyCodeBlockOrEmpty
        data={data.requestData}
        header={isGraphql ? t('variables') : (bodyLabel ?? t('body'))}
        emptyMessage={t('requestEmpty')}
      />
      {data.requestFiles && data.requestFiles.length > 0 && (
        <Stack space={2}>
          <Text font="bold14" color="textSecondary" text={t('files')} />
          {data.requestFiles.map((name) => (
            <Text key={name} font="regular14" text={name} />
          ))}
        </Stack>
      )}
      {data.requestHeaders && Object.keys(data.requestHeaders).length > 0 && (
        <CodeBlock header={headersLabel ?? t('headers')} data={data.requestHeaders} />
      )}
    </>
  );
};

const ResponseSection = ({ data }: { data: RequestDetailsData }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={3}>
      <Text font="heading2" text={t('response')} />
      <BodyCodeBlockOrEmpty
        data={data.responseData}
        header={t('body')}
        emptyMessage={t('responseEmpty')}
      />
      {data.responseHeaders && Object.keys(data.responseHeaders).length > 0 && (
        <CodeBlock header={t('headers')} data={data.responseHeaders} />
      )}
    </Stack>
  );
};

const BodyCodeBlockOrEmpty = ({
  data,
  header,
  emptyMessage,
}: {
  data: unknown;
  header: string;
  emptyMessage: string;
}) => {
  const hasData =
    data !== null &&
    data !== undefined &&
    data !== '' &&
    !(typeof data === 'object' && Object.keys(data as object).length === 0);

  if (!hasData) {
    return <Text font="regular14" color="textHelper" text={emptyMessage} />;
  }
  return <CodeBlock header={header} data={data as object} />;
};

const QueryBlock = ({ header, query }: { header: string; query: string }) => {
  const { copied, onCopy } = useClipboard();
  return (
    <QueryContainer>
      <HeaderFooterRow>
        <Text font="bold14" text={header} color="textSecondary" />
      </HeaderFooterRow>
      <EditorWrapper>
        <GraphQLEditor label="" initialValue={query} onChange={() => {}} isDisabled />
      </EditorWrapper>
      <HeaderFooterRow>
        <IconButton
          variant="ghost-purple"
          icon={copied ? <IconCheckmark /> : <IconCopyContent />}
          onClick={() => onCopy(query)}
        />
      </HeaderFooterRow>
    </QueryContainer>
  );
};

const QueryContainer = styled.div({
  border: `1px solid ${theme.color.border20}`,
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.uiTrans5,
});

const HeaderFooterRow = styled.div({
  padding: `${theme.space[2]} ${theme.space[3]}`,
});

const EditorWrapper = styled.div({
  borderTop: `1px solid ${theme.color.border20}`,
  borderBottom: `1px solid ${theme.color.border20}`,
  '.cm-content': {
    caretColor: 'transparent',
  },
  '.cm-editor': {
    border: 'none',
    borderRadius: 0,
    backgroundColor: 'transparent',
    maxHeight: pxToRem(384),
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  '.cm-editor.cm-focused': {
    border: 'none',
    backgroundColor: 'transparent',
    outline: 'none',
  },
  '.cm-editor.cm-focused .cm-gutters': {
    backgroundColor: 'inherit',
  },
});

const locale = {
  en: {
    request: 'Request',
    response: 'Response',
    body: 'Body',
    query: 'Query',
    variables: 'Variables',
    headers: 'Headers',
    files: 'Files',
    url: 'URL',
    requestEmpty: "This request doesn't have a body",
    responseEmpty: 'Empty response body',
  },
};
